import { useState } from "react";
import { useTranslation } from "react-i18next";
import LoginPage from "./LoginPage";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const APPRECIATIONS = ["", "Intéressé", "Pas intéressé", "Prolonger période d'essai", "Demande de devis"];

// ── Login Admin séparé (toujours en français) ─────────────────────────────────
function LoginAdmin({ onAdmin, onRetour }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [erreur, setErreur]     = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur(""); setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/login`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) { setErreur(data.detail || "Identifiants incorrects"); return; }
      if (!data.user?.is_admin) { setErreur("Accès réservé aux administrateurs"); return; }
      localStorage.setItem("geo5_token", data.access_token);
      onAdmin(data.user);
    } catch { setErreur("Impossible de contacter le serveur."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg,#0f172a,#1e3a5f)", fontFamily:"'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ background:"#fff", borderRadius:20, padding:40, width:"100%", maxWidth:400, boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:36, marginBottom:8 }}>⚙️</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#1a1a2e" }}>Administration</div>
          <div style={{ fontSize:13, color:"#6B7280", marginTop:4 }}>GEO5 Expert Advisor — CIVISOFT</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:13, fontWeight:700, color:"#1a1a2e", display:"block", marginBottom:6 }}>Email admin</label>
            <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@civisoft-fr.com"
              style={{ width:"100%", padding:"11px 13px", fontSize:14, border:"1.5px solid #E5E7EB", borderRadius:8, outline:"none", boxSizing:"border-box" }}/>
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:13, fontWeight:700, color:"#1a1a2e", display:"block", marginBottom:6 }}>Mot de passe</label>
            <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••••"
              style={{ width:"100%", padding:"11px 13px", fontSize:14, border:"1.5px solid #E5E7EB", borderRadius:8, outline:"none", boxSizing:"border-box" }}/>
          </div>
          {erreur && <div style={{ background:"#FEF2F2", border:"1px solid #DC2626", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#DC2626", marginBottom:16 }}>⚠️ {erreur}</div>}
          <button type="submit" disabled={loading}
            style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#1e3a5f,#2563eb)", border:"none", borderRadius:10, fontSize:15, fontWeight:800, color:"#fff", cursor:"pointer", marginBottom:12 }}>
            {loading ? "Connexion..." : "🔐 Accéder au panneau admin"}
          </button>
          <button type="button" onClick={onRetour}
            style={{ width:"100%", padding:"11px", background:"transparent", border:"1.5px solid #E5E7EB", borderRadius:10, fontSize:14, color:"#6B7280", cursor:"pointer" }}>
            ✕ Quitter
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Page publique d'accueil ───────────────────────────────────────────────────
const ADMIN_SECRET = "civisoft2026"; // clé secrète dans l'URL : ?admin=civisoft2026

export default function PagePublique({ onLogin, onAdmin }) {
  const { t } = useTranslation();

  // Détection URL secrète admin
  const urlParams = new URLSearchParams(window.location.search);
  const isAdminUrl = urlParams.get("admin") === ADMIN_SECRET;

  const [vue, setVue] = useState(isAdminUrl ? "loginAdmin" : "accueil");

  if (vue === "loginUser")  return <LoginPage onLogin={onLogin} onRetour={() => setVue("accueil")} />;
  if (vue === "loginAdmin") return <LoginAdmin onAdmin={onAdmin} onRetour={() => setVue("accueil")} />;

  const FEATURES = [
    { icon:"🔍", titre: t("pagepublique.features.analyse_titre"),    desc: t("pagepublique.features.analyse_desc") },
    { icon:"⚖️", titre: t("pagepublique.features.comparaison_titre"), desc: t("pagepublique.features.comparaison_desc") },
    { icon:"🤖", titre: t("pagepublique.features.agent_titre"),       desc: t("pagepublique.features.agent_desc") },
    { icon:"📄", titre: t("pagepublique.features.rapports_titre"),    desc: t("pagepublique.features.rapports_desc") },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#0a1628 0%,#0d2137 50%,#091520 100%)", fontFamily:"'Segoe UI', system-ui, sans-serif", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>

      {/* Logo + Titre */}
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div style={{ fontSize:64, marginBottom:16 }}>🏗️</div>
        <div style={{ color:"#fff", fontSize:36, fontWeight:900, letterSpacing:-1, marginBottom:8 }}>{t("app.titre")}</div>
        <div style={{ color:"rgba(255,255,255,0.6)", fontSize:16, marginBottom:4 }}>{t("app.slogan")}</div>
        <div style={{ color:"rgba(255,255,255,0.35)", fontSize:13 }}>{t("app.copyright")}</div>
      </div>

      {/* Cards pub */}
      <div style={{ display:"flex", gap:20, flexWrap:"wrap", justifyContent:"center", marginBottom:48, maxWidth:900 }}>
        {FEATURES.map((c,i) => (
          <div key={i} style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:"20px 24px", width:200, textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:10 }}>{c.icon}</div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:14, marginBottom:6 }}>{c.titre}</div>
            <div style={{ color:"rgba(255,255,255,0.5)", fontSize:12, lineHeight:1.5 }}>{c.desc}</div>
          </div>
        ))}
      </div>

      {/* Bouton unique — clients uniquement */}
      <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center" }}>
        <button onClick={() => setVue("loginUser")}
          style={{ padding:"16px 40px", background:"linear-gradient(135deg,#2D7A2D,#00A651)", border:"none", borderRadius:14, fontSize:17, fontWeight:800, color:"#fff", cursor:"pointer", boxShadow:"0 8px 32px rgba(45,122,45,0.4)", letterSpacing:0.3 }}>
          {t("pagepublique.lancer")}
        </button>
      </div>

      {/* Bouton Quitter */}
      <button onClick={() => { window.close(); }}
        style={{ marginTop:32, background:"transparent", border:"1.5px solid rgba(255,255,255,0.2)", borderRadius:10, padding:"10px 28px", fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.5)", cursor:"pointer" }}>
        {t("pagepublique.fermer")}
      </button>
      <div style={{ marginTop:8, fontSize:11, color:"rgba(255,255,255,0.25)" }}>
        {t("pagepublique.fermer_hint")}
      </div>
    </div>
  );
}
